import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Calendar,
  RefreshCw,
  Trash2,
  Eye,
  AlertCircle,
  CheckCircle,
  Clock,
  Loader
} from 'lucide-react';
import ConfirmDialog from '../Common/ConfirmDialog';
import { blotterService, Blotter } from '../../services/blotter';
import { formatDate } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/currencyUtils';

interface BlotterListProps {
  onRefresh?: () => void;
}

const BlotterList: React.FC<BlotterListProps> = ({ onRefresh }) => {
  const navigate = useNavigate();
  const [blotters, setBlotters] = useState<Blotter[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState<'all' | 'pending' | 'processing' | 'completed' | 'error'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({
    start_date: '',
    end_date: ''
  });
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    variant?: 'danger' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  const fetchBlotters = async () => {
    setLoading(true);
    try {
      const response = await blotterService.getBlotters({
        page: currentPage,
        per_page: 15,
        ...(filter !== 'all' && { status: filter as 'pending' | 'processing' | 'completed' | 'error' }),
        ...(searchTerm && { search: searchTerm }),
        ...(dateRange.start_date && { start_date: dateRange.start_date }),
        ...(dateRange.end_date && { end_date: dateRange.end_date })
      });

      setBlotters(response.data || []);
      setTotalPages(response.meta?.last_page || 1);
    } catch (error) {
      console.error('Failed to fetch blotters:', error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-hide success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    fetchBlotters();
  }, [currentPage, filter, searchTerm, dateRange]);

  // Auto-refresh when there are processing/pending blotters
  useEffect(() => {
    const hasActiveBlotters = blotters.some(b =>
      b.status === 'processing' || b.status === 'pending'
    );

    if (!hasActiveBlotters) return;

    // Poll every 3 seconds for active blotters
    const interval = setInterval(() => {
      fetchBlotters();
    }, 3000);

    return () => clearInterval(interval);
  }, [blotters]);

  const handleReprocess = (id: number) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Reprocess Blotter',
      message: 'Are you sure you want to reprocess this blotter? This will recalculate all transactions and commissions.',
      variant: 'warning',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));

        try {
          await blotterService.processBlotter(id);
          setSuccessMessage('Blotter is being reprocessed');
          fetchBlotters();
          if (onRefresh) onRefresh();
        } catch (error) {
          console.error('Failed to reprocess blotter:', error);
        }
      }
    });
  };

  const handleDelete = (id: number) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Blotter',
      message: 'Are you sure you want to delete this blotter? This action cannot be undone and will remove all associated transactions and commissions.',
      variant: 'danger',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));

        try {
          await blotterService.deleteBlotter(id);
          setSuccessMessage('Blotter deleted successfully');
          fetchBlotters();
          if (onRefresh) onRefresh();
        } catch (error) {
          console.error('Failed to delete blotter:', error);
        }
      }
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'processing':
        return <Loader className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      completed: 'bg-green-100 text-green-800',
      processing: 'bg-blue-100 text-blue-800',
      error: 'bg-red-100 text-red-800',
      pending: 'bg-gray-100 text-gray-800'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status as keyof typeof styles]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Success Message */}
      {successMessage && (
        <div className="m-6 mb-0 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2 text-green-700">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="p-6 border-b">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-semibold">Daily Blotters</h2>
          <button
            onClick={fetchBlotters}
            className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="space-y-4">
          <div className="flex gap-4">
            <input
              type="text"
              placeholder="Search by filename..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as typeof filter)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="error">Error</option>
            </select>
          </div>

          {/* Date Range Filter */}
          <div className="flex gap-4 items-center">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">From:</label>
              <input
                type="date"
                value={dateRange.start_date}
                onChange={(e) => setDateRange(prev => ({ ...prev, start_date: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">To:</label>
              <input
                type="date"
                value={dateRange.end_date}
                onChange={(e) => setDateRange(prev => ({ ...prev, end_date: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
            {(dateRange.start_date || dateRange.end_date) && (
              <button
                onClick={() => setDateRange({ start_date: '', end_date: '' })}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 underline"
              >
                Clear dates
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        ) : blotters.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No blotters found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {blotters.map(blotter => (
              <div
                key={blotter.id}
                className="border rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(blotter.status)}
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {blotter.original_filename}
                      </h3>
                      <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(blotter.date)}
                        </span>
                        <span>•</span>
                        <span>Uploaded by {blotter.uploader?.name || 'Unknown'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(blotter.status)}
                  </div>
                </div>

                {/* Statistics */}
                {blotter.status === 'completed' && (
                  <div className="grid grid-cols-4 gap-4 mb-3 p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Total Commission</div>
                      <div className="text-sm font-semibold text-gray-900">
                        {formatCurrency(blotter.total_commission)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Salesman</div>
                      <div className="text-sm font-semibold text-blue-600">
                        {formatCurrency(blotter.payout_commission)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">House</div>
                      <div className="text-sm font-semibold text-green-600">
                        {formatCurrency(blotter.net_commission)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Transactions</div>
                      <div className="text-sm font-semibold text-gray-900">
                        {blotter.total_trades}
                      </div>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {blotter.status === 'error' && blotter.error_message && (
                  <div className="mb-3 p-3 bg-red-50 rounded-lg">
                    <p className="text-sm text-red-700">{blotter.error_message}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-2">
                  <button
                    /* blotter.date is serialized as an ISO timestamp like
                       "2026-06-05T00:00:00.000000Z"; the report endpoint
                       validates against Y-m-d, so strip the time portion. */
                    onClick={() => navigate(`/reports/blotter?date=${blotter.date.slice(0, 10)}`)}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
                  >
                    <Eye className="w-4 h-4" />
                    View Details
                  </button>

                  {(blotter.status === 'error' || blotter.status === 'completed') && (
                    <button
                      onClick={() => handleReprocess(blotter.id)}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm border border-blue-300 text-blue-700 rounded hover:bg-blue-50"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Reprocess
                    </button>
                  )}

                  {blotter.status !== 'processing' && (
                    <button
                      onClick={() => handleDelete(blotter.id)}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-6">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default BlotterList;
